import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { useSnackbar } from '../../context/SnackbarContext';
import { sendEmailOTP } from '../../api/userProfile'; // <-- Import the connector

interface EmailOTPVerificationProps {
  email: string;
  loading: boolean;
  onOTPSubmit: (otp: string) => void;
  onBack: () => void;
}

const EmailOTPVerification = ({
  email,
  loading,
  onOTPSubmit,
  onBack
}: EmailOTPVerificationProps) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(60);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  const handleSubmit = () => {
    if (otp) {
      onOTPSubmit(otp);
    }
  };

  const handleResendOTP = () => {
    sendEmailOTP(email)
      .then(() => {
        setResendTimer(60);
        showSnackbar('OTP resent successfully', 'success');
      })
      .catch((err) => {
        showSnackbar(err.message || 'Failed to resend OTP', 'error');
      });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: '400px'
      }}
    >
      <Typography
        sx={{
          margin: '6px',
          fontWeight: '600',
          lineHeight: '24px',
          fontSize: '16px'
        }}
      >
        Please enter the OTP sent to {email}
      </Typography>
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="OTP Code"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
              backgroundColor: theme.palette.background.paper,
              '&:hover fieldset': {
                borderColor: theme.customColors.themePrimary
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.customColors.themePrimary
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.customColors.themePrimary
            }
          }}
        />
        <Button
          variant="text"
          sx={{
            ml: 1,
            textTransform: 'capitalize',
            display: 'flex'
          }}
          onClick={handleResendOTP}
          disabled={resendTimer > 0 || loading}
        >
          Resend {resendTimer > 0 ? `(${resendTimer}s)` : ''}
        </Button>
      </Box>
      <Button
        variant="contained"
        sx={{
          width: '100%',
          height: '40px',
          borderRadius: '20px',
          textTransform: 'none',
          fontSize: '14px',
          lineHeight: '24px',
          color: `${theme.customColors.themeSecondary} !important`,
          backgroundColor: theme.customColors.themePrimary,
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          ':hover': {
            color: `${theme.customColors.themeSecondary} !important`,
            backgroundColor: theme.customColors.themePrimary,
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
          },
          pointerEvents: loading ? 'none' : 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          marginTop: '10px'
        }}
        disabled={loading || !otp}
        onClick={handleSubmit}
      >
        {loading ? (
          <CircularProgress
            sx={{ color: theme.customColors.themeSecondary }}
            size={20}
          />
        ) : (
          'Verify'
        )}
      </Button>
      <Button
        variant="contained"
        sx={{
          width: '100%',
          height: '40px',
          borderRadius: '20px',
          textTransform: 'none',
          border: `1px solid ${theme.customColors.themePrimary}`,
          backgroundColor: theme.customColors.themeSecondary,
          color: theme.customColors.themePrimary,
          fontSize: '14px',
          lineHeight: '24px',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          ':hover': {
            border: `1px solid ${theme.customColors.themePrimary}`,
            backgroundColor: theme.customColors.themeSecondary,
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
          },
          pointerEvents: loading ? 'none' : 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          marginTop: '10px'
        }}
        onClick={onBack}
      >
        Back
      </Button>
    </Box>
  );
};

export default EmailOTPVerification;
